<template>
  <div v-if="!$apollo.queries.getAccountDetails.loading" data-test="account-details">
    <FlowHeading
      :title="$t('Enter your account details')"
      title-type="title1"
      :sub-title="$t('Please add your details to complete your Worksome account')"
    />

    <div v-if="!isFreelancer" class="wds-input-group">
      <WdsInput
        v-model="fullName"
        name="fullName"
        autocomplete="name"
        :error="errorFullName"
        :placeholder="$t('Full name')"
        :label="$t('Name')"
        lg
        required
        data-test="name-input"
        @focusout="v$.fullName.$touch"
      />
    </div>
    <div v-else class="wds-input-group">
      <WdsStack direction-column>
        <div class="wds-input-group tw-flex">
          <div class="tw-mr-2.5 tw-w-1/2">
            <WdsInput
              v-model="firstName"
              name="firstName"
              autocomplete="firstName"
              :error="errorFirstName"
              :placeholder="$t('First name')"
              :label="$t('First name')"
              required
              data-test="first-name-input"
              @focusout="v$.firstName.$touch"
            />
          </div>

          <div class="tw-w-1/2">
            <WdsInput
              v-model="middleName"
              name="middleName"
              autocomplete="middleName"
              :placeholder="$t('Middle name')"
              :label="$t('Middle name')"
              data-test="middle-name-input"
            />
          </div>
        </div>
        <WdsInput
          v-model="lastName"
          name="lastName"
          autocomplete="lastName"
          :error="errorLastName"
          :placeholder="$t('Last name')"
          :label="$t('Last name')"
          lg
          required
          data-test="last-name-input"
          @focusout="v$.lastName.$touch"
        />
      </WdsStack>
    </div>

    <div v-if="isCompany" class="wds-input-group">
      <WdsInput
        v-model="companyName"
        name="companyName"
        autocomplete="organization"
        :error="errorCompanyName"
        :placeholder="$t('Company name')"
        :label="$t('Company')"
        lg
        required
        data-test="account-name-input"
        @focusout="v$.companyName.$touch"
      />
    </div>

    <div v-if="!isCompany">
      <div class="wds-input-group">
        <WdsInput
          v-model="address"
          name="address"
          autocomplete="address-level1"
          :error="errorAddress"
          :placeholder="$t('Street name')"
          :label="$t('Address')"
          lg
          required
          @focusout="v$.address.$touch"
        />
      </div>

      <div class="wds-input-group tw-flex">
        <div class="tw-mr-2.5" style="width: 70%">
          <WdsInput
            v-model="city"
            name="city"
            autocomplete="address-level2"
            :error="errorCity"
            :placeholder="$t('City')"
            :label="$t('City')"
            lg
            required
            @focusout="v$.city.$touch"
          />
        </div>

        <div style="width: 30%">
          <WdsInput
            v-model="zipCode"
            name="zipCode"
            autocomplete="postal-code"
            :error="errorZipCode"
            :placeholder="$t('Post code')"
            :label="$t('Post code')"
            lg
            style="min-width: 100px"
            required
            @focusout="v$.zipCode.$touch"
          />
        </div>
      </div>
    </div>

    <div class="wds-input-group">
      <label>{{ $t('Country') }}</label>
      <WdsSelect
        :key="countryInputRerenderKey"
        :start-selected="countryCode"
        :error="errorCountryCode"
        :placeholder="$t('Where are you located?')"
        :list="Object.values(countryList)"
        required
        class="tw-mt-2"
        name="account-country-select"
        searchable
        @select-option="handleCountrySelected"
        @focusout="v$.countryCode.touch"
      />
    </div>
    <div v-if="stateCountries.includes(countryCode.toUpperCase())" class="wds-input-group">
      <label>{{ countryCode.toUpperCase() === 'CA' ? $t('Province') : $t('State') }}</label>
      <WdsSelect
        class="tw-mt-2"
        :start-selected="stateCode"
        :error="errorStateCode"
        :placeholder="$t('Select')"
        :list="stateList[countryCode]"
        name="account-state-select"
        @select-option="handleStateSelected"
      />
    </div>

    <InputWithInfo :info-title="$t(`What's a market?`)" :label="$t('Market')">
      <template #info-text>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="marketInfoText"></div>
      </template>

      <WdsSelect
        :key="marketInputRerenderKey"
        :start-selected="marketCode"
        class="tw-mt-2"
        :error="errorMarketCode"
        :warning="warningMarketName"
        placeholder="Select"
        :list="Object.values(marketList)"
        :disabled="marketDisabled"
        data-test="account-market-select"
        name="account-market-select"
        @select-option="handleMarketSelected"
        @focusout="v$.marketCode.touch"
      />
    </InputWithInfo>

    <label>{{ $t('Phone number') }}</label>
    <InputTelephone
      class="tw-mb-4"
      :country-code="countryCode"
      :persistent-phone-number="phone"
      :invalid-phone-number-error="errorPhoneNumber"
      @phone-changed="handlePhoneChanged"
      @validation-updated="handlePhoneValidity"
    />

    <div class="tw-mt-4 tw-mb-8">
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsCheckbox v-model:checked="canNotify" :value="canNotify" data-test="account-can-notify">
        <span class="c-gray-600">{{ $t('Notify me via SMS about new messages and jobs.') }}</span>
      </WdsCheckbox>
    </div>

    <div class="tw-mt-6">
      <WdsButton
        full-width
        lg
        primary
        :disabled="!canSubmit"
        data-test="account-submit-button"
        @click="update"
      >
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { WdsInput, WdsButton, WdsSelect, WdsStack } from '@worksome/wds-components'
import gql from 'graphql-tag'
import { replace } from 'lodash'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import InputWithInfo from '@/components/inputs/InputWithInfo.vue'
import { useValidatorWithGraphQlError, useValidatorError } from '@/composables/errors'
import { FormValidation } from '@/utils/formValidation'
import InputTelephone from '@/components/InputTelephone.vue'
import { $t } from '@/composables/translation'

export default {
  name: 'AccountDetails',

  components: {
    InputTelephone,
    ButtonSpinner,
    WdsButton,
    WdsInput,
    WdsSelect,
    WdsStack,
    FlowHeading,
    InputWithInfo,
  },

  inject: ['next'],

  props: {
    onClick: {
      type: Function,
      require: false,
      default: () => {},
    },
    countryList: {
      type: Object,
      default: () => {},
    },
    stateList: {
      type: Array,
      default: () => [],
    },
    marketList: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['save'],

  setup() {
    const v$ = useVuelidate()
    const errors = ref({})

    const errorFullName = useValidatorError(v$, 'fullName')
    const errorFirstName = useValidatorError(v$, 'firstName')
    const errorLastName = useValidatorError(v$, 'lastName')
    const errorCompanyName = useValidatorError(v$, 'companyName')
    const errorAddress = useValidatorError(v$, 'address')
    const errorCity = useValidatorError(v$, 'city')
    const errorZipCode = useValidatorError(v$, 'zipCode')
    const errorStateCode = useValidatorWithGraphQlError(v$, errors, 'stateCode', 'state')
    const errorCountryCode = useValidatorWithGraphQlError(v$, errors, 'countryCode', 'country')
    const errorMarketCode = useValidatorWithGraphQlError(v$, errors, 'marketCode', 'market')
    const errorPhoneNumber = useValidatorWithGraphQlError(v$, errors, 'phone', 'phone')

    return {
      v$,
      errors,
      errorFullName,
      errorFirstName,
      errorLastName,
      errorCompanyName,
      errorAddress,
      errorCity,
      errorZipCode,
      errorStateCode,
      errorCountryCode,
      errorMarketCode,
      errorPhoneNumber,
    }
  },

  data() {
    return {
      globalId: '',
      activeUserId: 0,
      localId: 0,
      marketCode: '',
      accountType: '',
      fullName: '',
      firstName: '',
      middleName: '',
      lastName: '',
      companyName: '',
      phone: '',
      phoneIsValid: false,
      email: '',
      address: '',
      zipCode: '',
      city: '',
      countryCode: '',
      stateCode: '',
      canNotify: false,
      myAccount: {},
      form: {},
      countryMarkets: launchedCountriesByMarket,
      marketInputRerenderKey: 0,
      countryInputRerenderKey: 0,
      isSubmitting: false,
      warningMarketName: null,
      // global variable supplied in base.blade.php coming from cloudflare
      countryBasedOnIP: clientHttpCountry,
      marketDisabled: false,
    }
  },
  computed: {
    isCompany() {
      return this.accountType === 'company'
    },
    isFreelancer() {
      return this.accountType === 'freelancer'
    },
    lacksRequiredFields() {
      return this.v$.$invalid
    },
    canSubmit() {
      return !this.isSubmitting
    },
    stateCountries() {
      return countriesWithStates
    },
    marketInfoText() {
      if (this.isCompany) {
        return this.$t(
          'The market corresponds to the country that you offer jobs in and drives the currency, compliance and language settings against your account. If the country is not among our supported markets, please <a href="#" class="intercomchat">chat to us</a>.',
        )
      }

      return this.$t(
        'The market corresponds to the country that you accept jobs in and drives the currency, compliance and language settings against your account. If your country is not among our supported markets, we suggest choosing the market of your client. If you need help, <a href="#" class="intercomchat">chat to us</a>.',
      )
    },
  },

  watch: {
    countryCode(value) {
      const countryKey = value?.toLowerCase()
      // find market of selected country if available
      const marketKey = this.countryMarkets[countryKey] ?? ''
      this.marketCode = this.marketList[marketKey]?.value ?? ''
      this.marketDisabled = !!this.marketCode
      this.warningMarketName = null
      this.marketInputRerenderKey += 1
    },
    marketCode(value) {
      const countryKey = this.countryCode?.toLowerCase()
      const countryMarketKey = this.countryMarkets[countryKey] ?? ''

      this.marketDisabled = !!value && value === countryMarketKey

      if (value && countryKey && value !== countryMarketKey) {
        this.warningMarketName = $t(
          'The market you chose is different from the country you are located in. Please make sure to sign up for the market that is closest to you, as it will affect your language and currency settings.',
        )
      } else {
        this.warningMarketName = null
      }
    },
  },

  mounted() {
    // if we get country from the server and it's in the list of our countries
    // if country is in list of market that would also be set as well
    if (
      this.countryBasedOnIP &&
      Object.keys(this.countryList).includes(this.countryBasedOnIP.toUpperCase())
    ) {
      this.countryCode = this.countryBasedOnIP.toUpperCase()
      this.countryInputRerenderKey += 1
    }
  },

  validations() {
    return {
      fullName: {
        requiredIf: helpers.withMessage(
          $t('Please enter your full name'),
          requiredIf(!this.isFreelancer),
        ),
        splitCheck: helpers.withMessage(
          $t('Please include first and last name'),
          (value) => !this.isCompany || value.trim().includes(' '),
        ),
      },

      firstName: {
        requiredIf: helpers.withMessage(
          $t('Please enter your first name'),
          requiredIf(this.isFreelancer),
        ),
      },

      lastName: {
        requiredIf: helpers.withMessage(
          $t('Please enter your last name'),
          requiredIf(this.isFreelancer),
        ),
      },

      companyName: {
        requiredIf: helpers.withMessage(
          $t('Please enter the company name'),
          requiredIf(this.isCompany),
        ),
      },

      address: {
        requiredIf: helpers.withMessage(
          $t('Please enter your address'),
          requiredIf(!this.isCompany),
        ),
      },

      city: {
        requiredIf: helpers.withMessage($t('Please enter your city'), requiredIf(!this.isCompany)),
      },

      zipCode: {
        requiredIf: helpers.withMessage(
          $t('Please enter your post code'),
          requiredIf(!this.isCompany),
        ),
      },

      countryCode: {
        required: helpers.withMessage($t('Please enter your country'), required),
      },

      stateCode: {
        requiredIf: helpers.withMessage(
          $t('Please enter your state'),
          requiredIf(this.stateCountries.includes(this.countryCode.toUpperCase())),
        ),
      },

      marketCode: {
        required: helpers.withMessage($t('Please enter your market'), required),
      },

      phone: {
        required: helpers.withMessage($t('Please enter your phone number'), required),

        valid: helpers.withMessage(
          $t('The given phone number is invalid'),
          () => this.phoneIsValid,
        ),
      },
    }
  },

  methods: {
    $t,
    async update() {
      this.isSubmitting = true
      const hasErrors = await this.v$.$validate()

      if (!hasErrors) {
        this.isSubmitting = false
        return
      }

      this.$emit('save', this.form)
      this.onClick()
      const promises = [this.updateAccountDetails(), this.updateUserInfo()]
      if (this.isCompany) {
        promises.push(this.updateCompanyInfo())
      }
      await Promise.allSettled(promises)

      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },

    async updateAccountDetails() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation update($input: UpdateAccountInput!) {
              updateAccount(input: $input) {
                globalId
                name
                market
                ... on ContactInformation {
                  phone
                  email
                }
                primaryLocation {
                  address
                  zipCode
                  city
                  country
                  state
                }
              }
            }
          `,
          variables: {
            input: {
              globalId: this.globalId,
              name: this.isCompany
                ? this.companyName
                : this.isFreelancer
                  ? replace('  ', ' ', `${this.firstName} ${this.middleName} ${this.lastName}`)
                  : this.fullName,
              firstName: this.firstName,
              middleName: this.middleName,
              lastName: this.lastName,
              market: this.marketCode,
              phone: this.phone,
              email: this.email,
              address: this.address,
              zipcode: this.zipCode,
              city: this.city,
              country: this.countryCode,
              state: this.stateCode,
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
          fetchPolicy: 'no-cache', // cache update causes flashing validation errors
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },

    async updateCompanyInfo() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation update($input: UpdateCompanyInput!) {
              updateCompany(input: $input) {
                globalId
                settings {
                  notify_sms
                }
              }
            }
          `,
          variables: {
            input: {
              globalId: this.globalId,
              settings: {
                notify_sms: this.canNotify,
              },
            },
          },
          fetchPolicy: 'no-cache', // cache update causes flashing validation errors
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },

    async updateUserInfo() {
      // Ensures that user does not update on behalf on someone else
      // Do not remove :)
      if (this.localId !== this.activeUserId) {
        return
      }

      await this.$apollo
        .mutate({
          mutation: gql`
            mutation update($input: UpdateUserInput!) {
              updateUser(input: $input) {
                localId
                name
                phone
                settings {
                  notify_sms
                }
              }
            }
          `,
          variables: {
            input: {
              localId: this.localId,
              name:
                this.fullName === ''
                  ? replace('  ', ' ', `${this.firstName} ${this.middleName} ${this.lastName}`)
                  : this.fullName,
              phone: this.phone,
              settings: {
                notify_sms: this.canNotify,
              },
            },
          },
          fetchPolicy: 'no-cache', // cache update causes flashing validation errors
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },

    handleCountrySelected(country) {
      if (country.value !== this.countryCode) {
        this.stateCode = ''
      }
      this.countryCode = country.value ?? ''
    },

    handleStateSelected(state) {
      this.stateCode = state.value ?? ''
    },

    handleMarketSelected(market) {
      this.marketCode = market.value ?? ''
    },

    handlePhoneChanged(val) {
      this.phone = val
    },
    handlePhoneValidity(val) {
      this.phoneIsValid = val
    },
  },

  apollo: {
    getMarketOfCompanyInvitingContact: {
      query: gql`
        query companyFreelancers($status: ContactStatus) {
          me {
            activeAccount {
              ... on Freelancer {
                companyFreelancers(filter: { status: $status }) {
                  data {
                    company {
                      market
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          status: 'APPLIED',
        }
      },
      result({ data }) {
        const info = data.me.activeAccount
        const market = info.companyFreelancers?.length ? info.companyFreelancers[0].market : null
        if (market) {
          /*
           * For these contacts, we prefill the market of the first company that
           * invited them.  While it is possible that multiple companies could invite the
           * same freelancer before they accept an invitation, the chance of this happening
           * _and_ involving companies in different markets is low.
           */
          this.marketCode = this.marketList[market]
        }
      },
      update: (data) => data.me,
    },

    getAccountDetails: {
      query: gql`
        query me {
          me {
            localId
            name
            settings {
              notify_sms
            }
            activeAccount {
              globalId
              nameRaw
              market
              user {
                localId
              }
              type: __typename
              ... on ContactInformation {
                phone
                email
              }
              primaryLocation {
                address
                zipCode
                city
                country
                state
              }
              ... on Company {
                settings {
                  notify_sms
                }
              }
              ... on Freelancer {
                firstName
                middleName
                lastName
              }
            }
          }
        }
      `,
      result({ data }) {
        const info = data.me.activeAccount
        this.localId = data.me.activeAccount.user.localId
        this.activeUserId = data.me.localId
        this.globalId = info.globalId
        this.accountType = info.type.toLowerCase() ?? this.accountType
        this.fullName = this.isCompany ? data.me.name : info.nameRaw ?? this.fullName

        if (this.isFreelancer) {
          this.firstName = data.me.activeAccount.firstName
          this.middleName = data.me.activeAccount.middleName
          this.lastName = data.me.activeAccount.lastName
        }

        this.companyName = info.nameRaw ?? this.companyName
        this.phone = info.phone ?? this.phone
        this.email = info.email ?? this.email
        this.address = info.primaryLocation?.address ?? this.address
        this.zipCode = info.primaryLocation?.zipCode ?? this.zipCode
        this.city = info.primaryLocation?.city ?? this.city

        // Give advantage to CDN if this is the first time the user is filling this form
        // we judge this by an existing required field, name
        if (info.primaryLocation?.country) {
          this.countryCode = info.primaryLocation?.country ?? this.countryCode
        }

        if (info.primaryLocation?.state) {
          this.stateCode = info.primaryLocation?.state ?? this.stateCode
        }

        // Give advantage to CDN if this is the first time the user is filling this form
        // we judge this by an existing required field, name
        if (info.market) {
          this.marketCode = info.market ?? this.marketCode
        }
        this.canNotify =
          (this.isCompany ? info.settings?.notify_sms : data.me.settings?.notify_sms) ??
          this.canNotify ??
          false
      },
      update: (data) => data.me,
    },
  },
}
</script>

<style scoped>
:deep(label) {
  @apply tw-mb-0;
}
label {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  margin-bottom: 0.5rem !important;
}
.wds-input-group {
  margin: 24px 0;
}
.wds-input-group > .wds-input-wrapper > .wds-input-label {
  margin-bottom: unset;
}
:deep(.wds-select-wrapper .wds-select-button-display) {
  padding: 0.5625rem 0.75rem;
}
</style>
